import React, { useRef } from 'react'
import { Button, Typography, Grid, Box, CardMedia, Container, useTheme, Paper } from '@mui/material'
import HeaderImage from 'static/images/keys.jpg'
import './styles.css'
import { useHousing } from './useHousing'
import { useFormControls } from './formActions'
import Testimonials from './Testimonials'
import { slideInFromRight } from './styles'
import HousingForm from './HousingForm'
import { useSelector } from 'react-redux'

const Housing = () => {
  const { signedIn } = useHousing()
  const theme = useTheme()
  const formRef = useRef(null)
  const { formData, formSubmitted, errors, handleSubmit, handleCreateAccount, handleChange } =
    useFormControls()

  const scrollToForm = () => {
    formRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  const hasSubmittedHousingInterest = useSelector(state => Boolean(state.candidate?.housingInterest))

  return (
    <>
      <Box sx={{ width: '100%', overflowX: 'hidden' }}>
        <Paper 
          elevation={0} 
          sx={{ 
            width: '100%', 
            mb: 4,
            p: { xs: 2, md: 3 },
            backgroundColor: theme.palette.background.dark,
            borderRadius: '0px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}
        >
          <Grid container justifyContent="center">
            <Grid item xs={11}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant='h5' sx={{ 
                    color: 'white',
                    fontWeight: 'bold',
                    mb: 1
                  }}>
                    Relocation Assistance
                  </Typography>
                  <Typography variant='caption' sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    display: 'block',
                    mb: { xs: 2, md: 0 },
                    whiteSpace: 'normal',
                    wordBreak: 'normal'
                  }}>
                    {hasSubmittedHousingInterest 
                      ? "Thank you for your interest in our relocation services. Our team has received your request and will be in touch with you shortly."
                      : "Get expert help with your relocation needs. From finding a new home to settling into your community, our partners provide comprehensive support for medical professionals."}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <Grid container justifyContent="center">
          <Grid item xs={11}>
            <Box sx={{ mb: 4 }}>
              <Typography variant='h5' sx={{ mb: 2 }}>
                MyStethi has partnered with <a href="https://www.spyglassrealty.com/" target="_blank" rel="noopener noreferrer">Spyglass Relocation Services</a> to utilize the <a href="https://www.leadingre.com" target="_blank" rel="noopener noreferrer">Leading Real Estate Companies of the World® (LeadingRE)</a> network, connecting medical providers to the best realtors and services across the world! This connection not only makes your home search easier but also makes you eligible for savings of up to $2,000 on closing costs as part of the MyStethi community!
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Container sx={{ py: 5 }} ref={formRef}>
          {hasSubmittedHousingInterest ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant='h5' sx={{ mb: 2, color: theme.palette.primary.dark }}>
                Your Relocation Request is Being Processed
              </Typography>
              <Typography variant='body1' sx={{ mb: 3 }}>
                One of our relocation experts will be reaching out to you soon to discuss your potential move. 
                If you have any urgent questions, please don't hesitate to contact us.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={4} alignItems='center'>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    backgroundColor: theme.palette.grey[100],
                    p: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant='h5' sx={{ color: theme.palette.primary.dark, mb: 2 }}>
                    Pre-Decision Services
                  </Typography>
                  <Typography variant='body1' sx={{ mb: 2 }}>
                    * Personalized needs assessment <br />
                    * Tours of prospective move locations <br />
                    * Community comparison and cost of living insights <br />* Relocation resources
                  </Typography>
                  <Typography variant='h5' sx={{ color: theme.palette.primary.dark, mb: 2 }}>
                    Destination Services
                  </Typography>
                  <Typography variant='body1' sx={{ mb: 2 }}>
                    * Area orientation tours <br />
                    * Home search assistance for purchase or lease <br />
                    * Mortgage service recommendations <br />* Support for settling in
                  </Typography>

                  <Typography variant='h5' sx={{ color: theme.palette.primary.dark, mb: 2 }}>
                    Departure Services
                  </Typography>
                  <Typography variant='body1' sx={{ mb: 2 }}>
                    * Home sale assistance at your current location <br />* Move-out support
                  </Typography>

                  <Typography variant='h5' sx={{ color: theme.palette.primary.dark, mb: 2 }}>
                    Additional Services
                  </Typography>
                  <Typography variant='body1'>
                    * Moving and transportation recommendations <br />* Spouse and family support
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ fontSize: '10px', marginTop: '10px', fontStyle: 'italic' }}
                  >
                    MyStethi may receive a fee from referrals for relocation assistance
                  </Typography>
                </Box>
              </Grid>
              <HousingForm
                formSubmitted={formSubmitted}
                handleSubmit={handleSubmit}
                formData={formData}
                handleChange={handleChange}
                errors={errors}
                handleCreateAccount={handleCreateAccount}
                signedIn={signedIn}
              />
            </Grid>
          )}
        </Container>
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '800px',
          color: theme.palette.common.white,
        }}
      >
        <CardMedia
          component='img'
          height='800'
          image={HeaderImage}
          alt='Medical professionals finding homes'
          sx={{
            width: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: { xs: '0%', md: '10%' },
            left: { xs: '0%', md: 'auto' },
            transform: 'translate(0, -50%)',
            backgroundColor: theme.palette.action.disabledBackground,
            padding: 3,
            borderRadius: 2,
            zIndex: 2,
            width: { xs: '90%', sm: '60%', md: '40%' },
            margin: { xs: '0 auto' },
            animation: `${slideInFromRight} 1s ease-in-out forwards`,
          }}
        >
          <Typography variant='h2' gutterBottom color='white' textAlign='center'>
            Relocate with Confidence
          </Typography>
          <Typography variant='h6' color='white' sx={{ mb: 3 }} textAlign='center'>
            {hasSubmittedHousingInterest 
              ? "Your relocation journey is in good hands. Our team is working to provide you with the best possible support for your move."
              : "Whether you're buying, selling, or renting, relocating is the next big step after landing your job. Let MyStethi guide you through the process with trusted partnerships designed specifically for medical professionals. Enjoy exclusive benefits tailored just for you."}
          </Typography>
          {!hasSubmittedHousingInterest && (
            <Button
              variant='contained'
              sx={{
                mt: 3,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                display: 'block',
                mx: 'auto',
              }}
              onClick={scrollToForm}
            >
              Learn More
            </Button>
          )}
        </Box>
      </Box>

      <Testimonials />
    </>
  )
}

export default Housing
